<template>
  <!-- Existing blocks in muted grey, this order striped blue (red when over), light grey for what
       is left. Grey on purpose: the new segment is the only colour on the bar (spec). -->
  <span class="relative block h-2.5 overflow-hidden rounded border bg-gray-100" :class="over ? 'border-red-200' : 'border-gray-200'" role="img" :aria-label="ariaLabel">
    <span
      v-for="segment in segments"
      :key="segment.key"
      class="absolute bottom-0 top-0 border-l border-white first:border-l-0"
      :class="segment.reservation ? 'bg-gray-200' : 'bg-slate-300'"
      :style="{ left: segment.left, width: segment.width }"
      :title="segment.title"
    />
    <span
      v-if="value > 0"
      class="absolute bottom-0 top-0 split-bar__mine"
      :class="over ? 'bg-red-600' : 'bg-blue-600'"
      :style="{ left: mineLeft, width: mineWidth }"
      :title="mineTitle"
    />
  </span>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import { useCalendarPrivacyStore } from '@/store/calendarPrivacyStore';
  import type { SplitType, SplitWeek } from '@/utils/splitAcrossWeeks';

  const props = defineProps<{ week: SplitWeek; type: SplitType; value: number }>();

  const privacy = useCalendarPrivacyStore();

  const FULL = 100;

  const remaining = computed(() => props.week.remainingByType[props.type.capacityTypeId] ?? 0);
  const booked = computed(() => props.week.bookedByType[props.type.capacityTypeId] ?? []);
  const used = computed(() => booked.value.reduce((sum, booking) => sum + booking.quantity, 0));
  // planningCapacity = remaining + already booked; the bar is drawn against it.
  const total = computed(() => Math.max(1, remaining.value + used.value));
  // A week already over before this dialog opened is not this order's doing: the bar turns
  // red only once this order places something in it (spec: cell > 0 and cell > remaining).
  const over = computed(() => props.value > 0 && remaining.value - props.value < 0);

  const pct = (quantity: number): string => `${Math.min(FULL, (quantity / total.value) * FULL).toFixed(2)}%`;

  const segments = computed(() => {
    let cursor = 0;
    return booked.value.map((booking, index) => {
      const left = cursor;
      cursor += booking.quantity;
      return {
        key: `${index}-${booking.poNumber ?? 'res'}`,
        left: pct(left),
        width: pct(booking.quantity),
        reservation: booking.poNumber === null,
        title: privacy.hideCustomerMappings
          ? `${booking.quantity.toLocaleString()} ${props.type.code}`
          : `${booking.poNumber ?? booking.notes ?? 'Reservation'} · ${booking.quantity.toLocaleString()} ${props.type.code}`,
      };
    });
  });

  const mineLeft = computed(() => pct(used.value));
  const mineWidth = computed(() => pct(props.value));
  const mineTitle = computed(() => `This order · ${props.value.toLocaleString()} ${props.type.code}`);
  const ariaLabel = computed(
    () =>
      `${props.type.code}: ${used.value.toLocaleString()} booked, ${props.value.toLocaleString()} this order, ${Math.max(0, remaining.value - props.value).toLocaleString()} left`
  );
</script>

<style scoped>
  .split-bar__mine {
    background-image: repeating-linear-gradient(135deg, rgb(255 255 255 / 28%) 0 4px, transparent 4px 8px);
  }
</style>
